import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState';
import { useContentAppearance } from '@confluence/content-appearance/entry-points/ContentAppearanceContext';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError';
import { containsNetworkError } from '@confluence/network';
import { expValEquals } from '@confluence/feature-experiments';

import { EditorAppearanceButtonComponent } from './EditorAppearanceButtonComponent';
import { EditorAppearanceWithMaxWidth } from './EditorAppearanceWithMaxWidth';
import type { UpdateContentAppearancePropertyBody } from './editorAppearanceTypes';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { UpdateContentAppearancePropertyMutation } from './UpdateContentAppearancePropertyMutation.experimentalgraphql';

export const i18n = defineMessages({
	errorFlagTitle: {
		id: 'editor-appearance-button.error.flag.title',
		description:
			'Title for the error flag shown when there is a conflict updating the width of the content',
		defaultMessage: 'Unable to update width',
	},
	unauthorizedErrorFlagBody: {
		id: 'editor-appearance-button.unauthorized.error.flag.body',
		description:
			'Body text for the error flag shown when there is unauthorized error updating the width of the content',
		defaultMessage:
			'You don’t have permission to update the width. Contact your admin to request access.',
	},
	networkErrorFlagBody: {
		id: 'editor-appearance-button.network.error.flag.body',
		description: "Message for error flags when an error with a user's internet connection occurs",
		defaultMessage: 'Check your internet connection, refresh, and try again.',
	},
});

export type EditorAppearanceButtonNextProps = {
	contentId: string;
	isStatusDisconnected: boolean;
	flags: FlagsStateContainer;
	disabled?: boolean;
};

const EditorAppearanceButtonNextComponent = ({
	contentId,
	isStatusDisconnected,
	flags,
	disabled = false,
}: EditorAppearanceButtonNextProps) => {
	const { contentAppearance, setContentAppearance, contentAppearanceLoading } =
		useContentAppearance();

	const [{ collabEditProvider, collabEditInitialized }] = useNativeCollabState();
	const updateContentAppearanceProperty = async (
		params: UpdateContentAppearancePropertyBody,
	): Promise<void> => {
		const client = getApolloClient();
		const payload = {
			contentId: params.contentId,
			propertyKey: params.propertyKey,
			propertyValue: params.propertyValue,
			versionNumber: params.versionNumber.toString(),
			isDelete: false,
		};

		try {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			const result = await client.mutate({
				mutation: UpdateContentAppearancePropertyMutation,
				variables: payload,
			});

			if (result?.data?.experimentalUpdateContentProperty?.success) {
				setContentAppearance({
					appearance: params.propertyValue,
					version: params.versionNumber,
				});
			}
		} catch (error) {
			const is409Error = isStatusCodeError(error, '409');
			const is403Error = isUnauthorizedError(error);
			const isNetworkError = containsNetworkError(error);

			// Not showing flag for 409s since it's not a user error but something that's happening on our end
			if (is409Error) {
				markErrorAsHandled(error);
				return;
			}

			if (is403Error || isNetworkError) {
				markErrorAsHandled(error);
				const flagBodyText = is403Error
					? i18n.unauthorizedErrorFlagBody
					: i18n.networkErrorFlagBody;

				void flags.showErrorFlag({
					id: 'content-appearance-button-conflict-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...flagBodyText} />,
				});
			}
		}
	};

	const isDisabled = contentAppearanceLoading || !collabEditInitialized || disabled;

	if (expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true)) {
		return (
			<EditorAppearanceWithMaxWidth
				contentId={contentId}
				collabEditProvider={collabEditProvider}
				isDisabled={isDisabled}
				isStatusDisconnected={isStatusDisconnected}
				updateContentAppearanceProperty={updateContentAppearanceProperty}
				contentAppearanceDraft={contentAppearance}
				setContentAppearance={setContentAppearance}
			/>
		);
	}

	return (
		<EditorAppearanceButtonComponent
			contentId={contentId}
			collabEditProvider={collabEditProvider}
			isDisabled={isDisabled}
			isStatusDisconnected={isStatusDisconnected}
			updateContentAppearanceProperty={updateContentAppearanceProperty}
			contentAppearanceDraft={contentAppearance}
			setContentAppearance={setContentAppearance}
		/>
	);
};

export const EditorAppearanceButtonNext = withFlags(EditorAppearanceButtonNextComponent);
